import { isNumber } from 'lodash';
import type { GoToRule, Group, Poll } from 'schema/poll';
import type { ICState } from 'schema/state';
import {
  getQuestionGroupId,
  getQuestionSetId,
  selectDisplayedOrAllGroupQuestions,
  selectDisplayedQuestions,
  selectGroup,
  selectGroupQuestions,
  selectQuestion,
  selectSet,
  selectSetScreens,
} from '../../selects';
import {
  getGroupQuestionsInStack,
  getLogicConditionContext,
  getSetScreensInStack,
  getTimerDistance,
  hasAnswersAfterRules,
  hasQuestionsAfterRules,
  isGoToApply,
  isGroupRulesApply,
  isQuestionRulesApply,
  isRulesApply,
  isSetRulesApply,
} from '../../utils';
import { reduceQuestion, reduceQuestionGroup, reduceSequentialQuestionGroup } from '../reducers';
import { findLastSetScreenIndex, findScreen, findScreenIndexSafe, pollToScreens, Screen } from './screens';

export function next(poll: Poll, prevState: ICState): ICState {
  const nextId = calculateNextTransitionId(poll, prevState);

  const stateWithEmptyId: ICState = { ...prevState, questionId: null, groupId: null, finalId: null };

  switch (nextId.type) {
    case 'final':
      return { ...stateWithEmptyId, finalId: nextId.finalId };
    case 'question':
      return reduceQuestion({ poll, prevState, questionId: nextId.questionId });
    case 'group':
      return reduceQuestionGroup({ poll, prevState, groupId: nextId.groupId });
    case 'sequentialGroup': {
      return reduceSequentialQuestionGroup({ prevState, poll, questionId: nextId.questionId });
    }
  }
}

interface TransitionIdToFinal {
  type: 'final';
  finalId: string;
}

interface TransitionIdToQuestion {
  type: 'question';
  questionId: string;
}

interface TransitionIdToGroup {
  type: 'group';
  groupId: string;
}

interface TransitionIdInSequentialGroup {
  type: 'sequentialGroup';
  questionId: string;
}

type TransitionID = TransitionIdToFinal | TransitionIdToQuestion | TransitionIdToGroup | TransitionIdInSequentialGroup;

function calculateNextTransitionId(poll: Poll, state: ICState): TransitionID {
  if (isGlobalTimerExpiredAndStrictTimer({ state, poll })) {
    return calculateTransitionIdToFinal({ state, poll });
  }

  const allGoToRules: GoToRule[] = selectDisplayedQuestions({ poll, state }).flatMap(it => it.data.goToRules ?? []);

  const context = getLogicConditionContext(state.answersStack, poll.questions, state.externalLogicRules);
  const firstApplyableGoToRule = allGoToRules.find(isGoToApply(context));

  if (firstApplyableGoToRule) {
    return calculateNextTransitionIdFromGoTo(poll, state, firstApplyableGoToRule);
  }

  const group = state.groupId ? selectGroup(poll, state.groupId) : null;
  if (state.groupId && group?.data.hasSequentialDisplay) {
    const nextId = calculateNextSequentialQuestionId({
      poll,
      group,
      answersStack: state.answersStack,
      externalLogicRules: state.externalLogicRules,
    });
    if (nextId) return { type: 'sequentialGroup', questionId: nextId };
  }

  const setId = getSetIdByState(poll, state);
  if (setId) {
    const nextTransition = calculateNextSetScreenId({
      answersStack: state.answersStack,
      externalLogicRules: state.externalLogicRules,
      setId,
      poll,
    });
    if (nextTransition) return nextTransition;
    return calculateTransitionIdAsNextShowableScreenStartingFrom({ setId }, poll, state);
  }

  return calculateTransitionIdAsNextShowableScreenStartingFrom(
    { groupId: state.groupId, questionId: state.questionId },
    poll,
    state,
  );
}

function calculateTransitionIdAsNextShowableScreenStartingFrom(
  position: Partial<Pick<ICState, 'groupId' | 'questionId'> & { setId: string }>,
  poll: Poll,
  state: ICState,
  screens: Screen[] = pollToScreens(poll),
): TransitionID {
  const nextScreenIndex = position.setId
    ? findLastSetScreenIndex(poll, screens, position.setId) + 1
    : findScreenIndexSafe(poll, screens, position) + 1;

  const firstShowableScreen = screens.slice(nextScreenIndex).find(screen => {
    const setId = getSetIdByState(poll, screen);
    const isSetCanShow = !setId || isSetRulesApply(poll, setId, state.answersStack, state.externalLogicRules);

    switch (screen.type) {
      case 'group': {
        return isSetCanShow && isGroupRulesApply(poll, screen.groupId, state.answersStack, state.externalLogicRules);
      }
      case 'question': {
        return (
          isSetCanShow && isQuestionRulesApply(poll, screen.questionId, state.answersStack, state.externalLogicRules)
        );
      }
    }
  });

  const screenSetId = firstShowableScreen ? getSetIdByState(poll, firstShowableScreen) : null;
  if (screenSetId) {
    const nextScreen = calculateNextSetScreenId({
      answersStack: state.answersStack,
      externalLogicRules: state.externalLogicRules,
      setId: screenSetId,
      poll,
    });
    if (nextScreen) return nextScreen;
  }

  switch (firstShowableScreen?.type) {
    case undefined: {
      return calculateTransitionIdToFinal({ state, poll });
    }
    case 'group': {
      return firstShowableScreen;
    }
    case 'question': {
      return firstShowableScreen;
    }
  }
}

function getSetIdByState(poll: Poll, state: { groupId: string | null } | { questionId: string | null }) {
  if ('questionId' in state && state.questionId) return getQuestionSetId(selectQuestion(poll, state.questionId), poll);
  if ('groupId' in state && state.groupId) return selectGroup(poll, state.groupId).setId;
  return null;
}

function calculateTransitionIdToFinal({ state, poll }: { state: ICState; poll: Poll }): TransitionIdToFinal {
  const firstFinal = poll.finals[0];
  if (!firstFinal) throw new Error('Impossible: No finals are found!');

  const defaultFinalId = poll.finals.find(final => final.data.isDefault === true)?.id ?? firstFinal.id;

  const context = getLogicConditionContext(state.answersStack, poll.questions, state.externalLogicRules);

  for (const final of poll.finals) {
    const logicOperationType = final.data.logicOperationType;
    const logicRules = final.data.logicRules;
    if (
      logicOperationType &&
      logicRules &&
      logicRules.length > 0 &&
      isRulesApply(logicRules, logicOperationType, context)
    ) {
      return { type: 'final' as const, finalId: final.id };
    }
  }

  return { type: 'final' as const, finalId: defaultFinalId };
}

function calculateNextTransitionIdFromGoTo(poll: Poll, state: ICState, goToRule: GoToRule): TransitionID {
  const screens = pollToScreens(poll);

  let goToScreen: Screen;
  switch (goToRule.goToType) {
    case 'final': {
      return calculateTransitionIdToFinal({ poll, state });
    }
    case 'question': {
      goToScreen = findScreen(poll, screens, { questionId: goToRule.questionId });
      break;
    }
    case 'group': {
      goToScreen = findScreen(poll, screens, { groupId: goToRule.groupId });
      break;
    }
    case 'set': {
      const nextTransition = calculateNextSetScreenId({
        externalLogicRules: state.externalLogicRules,
        answersStack: state.answersStack,
        setId: goToRule.setId,
        poll,
      });
      if (nextTransition) return nextTransition;
      return calculateTransitionIdAsNextShowableScreenStartingFrom(goToRule, poll, state, screens);
    }
  }

  const context = getLogicConditionContext(state.answersStack, poll.questions, state.externalLogicRules);

  switch (goToScreen.type) {
    case 'group': {
      const group = selectGroup(poll, goToScreen.groupId);
      const groupQuestions = selectDisplayedOrAllGroupQuestions(poll, group.id, state.answersStack);

      if (!hasQuestionsAfterRules(groupQuestions, context)) {
        return calculateTransitionIdAsNextShowableScreenStartingFrom(goToScreen, poll, state, screens);
      }
      return goToScreen;
    }
    case 'question': {
      const question = selectQuestion(poll, goToScreen.questionId);
      if (!hasAnswersAfterRules(question, context)) {
        return calculateTransitionIdAsNextShowableScreenStartingFrom(goToScreen, poll, state, screens);
      }
      return goToScreen;
    }
  }
}

function isGlobalTimerExpiredAndStrictTimer({ state, poll }: { state: ICState; poll: Poll }) {
  if (poll.hasTimer) {
    const { timerDuration } = poll;
    const { startedAt } = state;

    const isTimerExpired =
      isNumber(startedAt) && isNumber(timerDuration) ? getTimerDistance(startedAt, timerDuration) <= 0 : false;

    return isTimerExpired && !!poll.timerStrict;
  }
  return false;
}

/** Возвращает следующий экран из сета */
function calculateNextSetScreenId({
  poll,
  setId,
  answersStack,
  externalLogicRules,
}: {
  poll: Poll;
  setId: string;
  answersStack: ICState['answersStack'];
  externalLogicRules: ICState['externalLogicRules'];
}): TransitionID | undefined {
  const set = selectSet(poll, setId);

  const lastAnswerState = answersStack.at(-1);
  const currentQuestion = lastAnswerState ? selectQuestion(poll, lastAnswerState.id) : null;

  const setScreens = selectSetScreens(poll, setId);

  const currentQuestionGroupId = currentQuestion ? getQuestionGroupId(currentQuestion, poll.questionDecks) : undefined;
  const currentScreenIndex = currentQuestion
    ? setScreens.findIndex(screen =>
        screen.type === 'group' ? currentQuestionGroupId === screen.id : currentQuestion.id === screen.id,
      ) + 1
    : 0;

  const screenAfterCurrent = setScreens.slice(currentScreenIndex);

  const nextScreen = screenAfterCurrent.find(screen => {
    if (screen.type === 'group') return isGroupRulesApply(poll, screen.id, answersStack, externalLogicRules);
    return isQuestionRulesApply(poll, screen.id, answersStack, externalLogicRules);
  });

  const questionLimit = set.data.pageLimit ?? setScreens.length;
  const alreadyAnsweredGroupQuestions = getSetScreensInStack(poll, answersStack, setScreens);

  if (nextScreen && alreadyAnsweredGroupQuestions.length < questionLimit) {
    if (nextScreen.type === 'group') return { type: 'group', groupId: nextScreen.id };
    return { type: 'question', questionId: nextScreen.id };
  }
  return undefined;
}

function calculateNextSequentialQuestionId({
  poll,
  group,
  answersStack,
  externalLogicRules,
}: {
  poll: Poll;
  group: Group;
  answersStack: ICState['answersStack'];
  externalLogicRules: ICState['externalLogicRules'];
}) {
  const lastSequenceAnswer = answersStack.at(-1);

  const sequenceQuestions = selectGroupQuestions(poll, group.id);
  const currentQuestionIndexInSequence = lastSequenceAnswer
    ? sequenceQuestions.findIndex(it => it.id === lastSequenceAnswer.id) + 1
    : 0;

  const context = getLogicConditionContext(answersStack, poll.questions, externalLogicRules);

  const sequenceQuestionsAfterCurrentQuestion = sequenceQuestions.slice(currentQuestionIndexInSequence);
  const filteredSequenceQuestionsAfterCurrentQuestion = sequenceQuestionsAfterCurrentQuestion.filter(question => {
    const { logicRules = [], logicOperationType = 'AND' } = question.data;
    return isRulesApply(logicRules, logicOperationType, context) && hasAnswersAfterRules(question, context);
  });
  const nextSequentialQuestion = filteredSequenceQuestionsAfterCurrentQuestion.at(0);

  const questionLimit =
    group.data.questionLimit && group.data.questionLimit > 0 ? group.data.questionLimit : sequenceQuestions.length;

  const alreadyAnsweredGroupQuestions = getGroupQuestionsInStack(answersStack, sequenceQuestions);

  const isTimeExpired = lastSequenceAnswer?.isTimeExpired;

  if (
    nextSequentialQuestion &&
    alreadyAnsweredGroupQuestions.length < questionLimit &&
    !(group.data.timerStrict && isTimeExpired !== undefined)
  ) {
    return nextSequentialQuestion.id;
  }
  return undefined;
}
